import { useEffect, useRef, useState } from 'react';
import type { ReactElement } from 'react';
import { useNavigate } from 'react-router';
import { Plus } from 'lucide-react';
import { assetToast } from '@xdocs/i18n';
import type { Persona } from '@xdocs/schemas';
import { Button, ConfirmDialog, EmptyState, SearchInput, Skeleton, Tabs, cn, useDebouncedValue, useI18n, useToast } from '@xdocs/ui';
import { optionalProp } from '../../lib/optionalProp.js';
import { PersonaCard } from './PersonaCard.js';
import { PersonaFormModal } from './PersonaFormModal.js';
import { usePersonaMutations } from './usePersonaMutations.js';
import { usePersonasList } from './usePersonasList.js';
import { usePersonasT } from './usePersonasT.js';
import styles from './PersonasPage.module.css';

type PersonasTab = 'mine' | 'public';

const SKELETON_CARD_COUNT = 6;

/** `/personas` (F16): a persona **gallery** — swatch, name, description,
 * narration-sample quote, "Use in generation" — per FEATURES.md F16 ("persona
 * cards ... gallery") and design/app/24-personas.html, NOT the shared
 * `<DataTable>` (§8.3 owns tabular/record lists; a persona card gallery is a
 * different, spec-named surface, same category as `/discover`'s card grid). */
export function PersonasPage(): ReactElement {
  const { locale, t: tGlobal } = useI18n();
  const { t } = usePersonasT();
  const navigate = useNavigate();
  const toast = useToast();
  const [tab, setTab] = useState<PersonasTab>('mine');
  const list = usePersonasList(tab);
  const mutations = usePersonaMutations();

  const [qDraft, setQDraft] = useState(list.query.q ?? '');
  const debouncedQ = useDebouncedValue(qDraft, 250);
  // Tracks the last `q` value WE pushed via `onQueryChange` — mirrors
  // `<DataTable>`'s own identical debounce contract (E6 §5.9) so an external
  // reset (tab switch resetting `usePersonasList`'s query) can resync the
  // draft without the debounce round-trip echoing a stale value back.
  const lastPushedQ = useRef(list.query.q ?? '');

  const [formOpen, setFormOpen] = useState(false);
  const [editingPersona, setEditingPersona] = useState<Persona | null>(null);
  const [isImport, setIsImport] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<Persona | null>(null);
  const [deleteError, setDeleteError] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (debouncedQ === (list.query.q ?? '')) return;
    const { q: _currentQ, ...rest } = list.query;
    lastPushedQ.current = debouncedQ;
    list.onQueryChange(debouncedQ ? { ...rest, q: debouncedQ, page: 1 } : { ...rest, page: 1 });
    // Only re-run when the debounced draft changes; `list.query`/
    // `onQueryChange` intentionally excluded to avoid re-firing on
    // server-driven query updates (identical to `<DataTable>`'s contract).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedQ]);

  useEffect(() => {
    const externalQ = list.query.q ?? '';
    if (externalQ !== lastPushedQ.current) {
      lastPushedQ.current = externalQ;
      setQDraft(externalQ);
    }
  }, [list.query.q]);

  function openCreate(): void {
    setEditingPersona(null);
    setIsImport(false);
    setFormOpen(true);
  }

  function openEdit(persona: Persona): void {
    setEditingPersona(persona);
    setIsImport(false);
    setFormOpen(true);
  }

  function openImport(persona: Persona): void {
    setEditingPersona(persona);
    setIsImport(true);
    setFormOpen(true);
  }

  function useInGeneration(persona: Persona): void {
    // `apps/web/src/routes/generate/**` is out of this task's ownership and
    // doesn't read this param yet (no `useSearchParams`/`location.state` in
    // `GeneratePage.tsx` today) — same class of cross-boundary gap this repo
    // already documents elsewhere (see e.g. `features/sharing/WIRING.md`).
    // Passing it now costs nothing (an unread query param is inert) and is
    // the hook the Generate wizard's own task can pick up to fulfil F16's
    // "Use in generation" pre-fill.
    void navigate(`/generate?personaId=${encodeURIComponent(persona.id)}`);
  }

  async function handleDelete(): Promise<void> {
    if (!deleteTarget) return;
    setDeleteError(undefined);
    try {
      await mutations.remove(deleteTarget.id);
      toast.push(assetToast(locale, 'success', t('personas:toast.deleted.action'), deleteTarget.name));
      setDeleteTarget(null);
    } catch {
      setDeleteError(t('personas:delete.error'));
    }
  }

  function clearSearch(): void {
    setQDraft('');
  }

  const isEmpty = !list.isLoading && !list.isError && list.rows.length === 0;
  const hasQuery = Boolean(list.query.q);

  return (
    <div className={cn(styles['page'])}>
      <div className={cn(styles['header'])}>
        <div>
          <p className={cn(styles['eyebrow'])}>{t('personas:page.eyebrow')}</p>
          <h1 className={cn(styles['title'])}>{t('personas:page.title')}</h1>
        </div>
        {tab === 'mine' && (
          <Button variant="primary" leadingIcon={<Plus size={16} aria-hidden="true" />} onClick={openCreate}>
            {t('personas:action.new')}
          </Button>
        )}
      </div>

      <div className={cn(styles['toolbar'])}>
        <Tabs<PersonasTab>
          label={t('personas:page.title')}
          value={tab}
          onChange={setTab}
          items={[
            { value: 'mine', label: t('personas:tabs.mine') },
            { value: 'public', label: t('personas:tabs.public') },
          ]}
        />
        <SearchInput
          label={t('personas:list.searchLabel')}
          hideLabel
          placeholder={t('personas:list.searchPlaceholder')}
          value={qDraft}
          onChange={(event) => setQDraft(event.target.value)}
          onClear={clearSearch}
          clearLabel={tGlobal('common:action.clearFilters')}
          containerClassName={cn(styles['search'])}
        />
      </div>

      {list.isError && (
        <EmptyState kind="error" title={t('personas:list.error.title')} action={{ label: t('personas:action.retry'), onClick: list.onRetry }} />
      )}

      {!list.isError && isEmpty && !hasQuery && tab === 'mine' && (
        <EmptyState
          kind="first-use"
          title={t('personas:list.empty.title')}
          description={t('personas:list.empty.description')}
          action={{ label: t('personas:list.empty.action'), onClick: openCreate }}
        />
      )}

      {!list.isError && isEmpty && !hasQuery && tab === 'public' && (
        <EmptyState kind="first-use" title={t('personas:list.empty.public.title')} description={t('personas:list.empty.public.description')} />
      )}

      {!list.isError && isEmpty && hasQuery && (
        <EmptyState
          kind="no-results"
          title={t('personas:list.noResults.title', { query: list.query.q ?? '' })}
          description={t('personas:list.noResults.description')}
          action={{ label: tGlobal('common:action.clearFilters'), onClick: clearSearch }}
        />
      )}

      {list.isLoading && list.rows.length === 0 && (
        <div className={cn(styles['grid'])} aria-hidden="true">
          <Skeleton shape="card" count={SKELETON_CARD_COUNT} />
        </div>
      )}

      {!list.isError && list.rows.length > 0 && (
        <div className={cn(styles['grid'])} role="list" aria-label={t('personas:page.title')}>
          {list.rows.map((persona) => (
            <div role="listitem" key={persona.id}>
              <PersonaCard
                persona={persona}
                scope={tab}
                onEdit={openEdit}
                onDelete={setDeleteTarget}
                onImport={openImport}
                onUseInGeneration={useInGeneration}
              />
            </div>
          ))}
        </div>
      )}

      {!list.isError && list.nextCursor && (
        <div className={cn(styles['loadMore'])}>
          <Button variant="secondary" state={list.isLoadingMore ? 'loading' : 'idle'} onClick={list.onLoadMore}>
            {tGlobal('common:action.loadMore')}
          </Button>
        </div>
      )}

      <PersonaFormModal
        open={formOpen}
        onOpenChange={(open) => {
          setFormOpen(open);
          if (!open) return;
        }}
        persona={editingPersona}
        isImport={isImport}
        onCreate={async (input) => {
          const result = await mutations.create(input);
          toast.push(assetToast(locale, 'success', t(isImport ? 'personas:toast.imported.action' : 'personas:toast.created.action'), result.name));
          return result;
        }}
        onUpdate={async (input) => {
          const result = await mutations.update(input);
          toast.push(assetToast(locale, 'success', t('personas:toast.updated.action'), result.name));
          return result;
        }}
      />

      <ConfirmDialog
        open={deleteTarget !== null}
        onOpenChange={(open) => !open && setDeleteTarget(null)}
        action={t('personas:list.action.delete')}
        assetName={deleteTarget?.name ?? ''}
        destructive
        loading={mutations.isDeleting}
        {...optionalProp('error', deleteError)}
        onConfirm={() => void handleDelete()}
      />
    </div>
  );
}
